import { html, View } from 'rune-ts';
import { ProductListTypeS } from '../../../../../features/ProductList/outbound/share';
import klass from './LimitedEdition.module.scss';
import {
  SectionTitle,
  SectionTitleData,
} from '@services/MarppleShop/renderApp/components/cells/SectionTitle/SectionTitle';
import {
  ArrowPlugin,
  SeeMoreLinkPlugin,
} from '@services/MarppleShop/renderApp/components/cells/SectionTitle/plugins';
import { SwiperView } from '@services/MarppleShop/renderApp/components/cells/Swiper/Swiper.ts';
import {
  ProductCardView,
  ProductLikeToggleEvent,
} from '@services/MarppleShop/renderApp/components/cells/ProductCard/ProductCardView.ts';
import {
  ProductCardViewData,
  ProductCardSize,
  ProductCardTheme,
} from '@services/MarppleShop/features/ProductList/type';
import { PostMessageNavigate } from '@services/MarppleShop/shared/app/navigate.ts';
import { makeSwiperAutoPlayOption } from '@services/MarppleShop/renderApp/pages/Main/utils/swiper-option.ts';
import { LimitedEditionVisibleHelper } from './helper/LimitedEditionVisibleHelper';

interface LimitedEditionSectionTitle {
  getTitle(): string;
  getSubTitle?(): string;
  getSeeMoreLink(): string;
  getNavigate(): PostMessageNavigate;
}

export type LimitedEditionProps = {
  count: number | null;
  products: ProductListTypeS.ProductCardViewData[];
};

export interface LimitedEditionOption {
  is_mobile: boolean;
  is_see_more_link_visible?: boolean;
}

export class LimitedEdition extends View<LimitedEditionProps> {
  private readonly swiperView: SwiperView<ProductCardViewData, typeof ProductCardView>;

  private readonly visibleHelper: LimitedEditionVisibleHelper;

  private readonly plugins: NonNullable<SectionTitleData['plugins']> = [];

  constructor(
    data: LimitedEditionProps,
    private option: LimitedEditionOption,
    private readonly sectionTitle: LimitedEditionSectionTitle,
  ) {
    super(data, option);

    this.visibleHelper = new LimitedEditionVisibleHelper(data, option);

    // section title plugins
    if (this.visibleHelper.isSeeMoreLinkVisible()) {
      const seeMoreLinkPlugin = new SeeMoreLinkPlugin({
        navigate: this.sectionTitle.getNavigate(),
        see_more_link: this.sectionTitle.getSeeMoreLink(),
        size: option.is_mobile ? 'xSmall' : 'medium',
        theme: 'dark',
      });
      this.plugins.push(seeMoreLinkPlugin);
    }
    if (this.visibleHelper.isArrowVisible()) {
      const arrowPlugin = new ArrowPlugin({
        theme: 'dark',
        size: option.is_mobile ? 'large' : 'xLarge',
        leftClickFn: () => {
          this.swiperView.prev();
        },
        rightClickFn: () => {
          this.swiperView.next();
        },
      });
      this.plugins.push(arrowPlugin);
    }

    this.swiperView = new SwiperView(
      makeSwiperAutoPlayOption({
        is_mobile: this.option.is_mobile,
      }),
      {
        swiper: klass.product_cards_swiper,
        wrapper: klass.product_cards_container,
      },
      ProductCardView,
      this.data.products,
      {
        resize_width: 600,
        size: this.option.is_mobile ? ProductCardSize.small : ProductCardSize.large,
        theme: ProductCardTheme.dark,
        card_klass: klass.product_card,

        // is_lazy 좌우로 적용하는 옵션도 필요할 듯 ...
        is_lazy: false,
      },
    );
  }

  override template() {
    const { is_mobile } = this.option;

    return html`
      <div class="${klass.container}">
        <div class="${klass.content_wrap}">
          <!-- 타이틀 -->
          <div class="${klass.title_wrap}">
            ${new SectionTitle({
              title_props: {
                title: this.sectionTitle.getTitle(),
                sub_title: this.sectionTitle.getSubTitle ? this.sectionTitle.getSubTitle() : '',
                size: is_mobile ? 'medium' : 'xLarge',
                theme: 'dark',
              },
              plugins: this.plugins,
            })}
          </div>

          <!-- 상품 목록 with swiper -->
          ${this.swiperView}
        </div>
      </div>
    `;
  }

  protected override onRender(): void {
    super.onRender();

    this.delegate(ProductLikeToggleEvent, ProductCardView, async (_, view) => {
      await view.toggleLikeButton();
    });
  }
}
